import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from "react";
import { Box, Button, TextField, Typography, makeStyles, CircularProgress } from "@material-ui/core";
import EmailIcon from "@material-ui/icons/Email";
import GetAppIcon from "@material-ui/icons/GetApp";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import { defineMessages, useIntl } from "react-intl";
import { addRegisteredSectionLayout } from "../sectionLayoutDescriptions";
const UNIT_TOKENS = new Set(["kr", "%", "km", "mnd", "år", "nok"]);
const SUMMARY_DOWNLOAD_FILENAME = "Oppsummering.pdf";
const normalizeText = (value) => value.replace(/\s+/g, " ").trim();
const parseSummaryRows = (lines) => {
    const rows = [];
    for (let i = 0; i < lines.length; i++) {
        const current = lines[i];
        if (current.startsWith("*")) {
            rows.push({ label: "", value: current, isNote: true });
            continue;
        }
        const next = lines[i + 1];
        if (!next) {
            rows.push({ label: current, value: "" });
            continue;
        }
        let value = next;
        i += 1;
        const unit = lines[i + 1];
        if (unit && UNIT_TOKENS.has(unit.toLowerCase())) {
            value = `${value} ${unit}`;
            i += 1;
        }
        rows.push({ label: current, value });
    }
    return rows;
};
const generateSummaryPayload = (data) => {
    const { dateTime, rows } = data;
    return {
        generatedDateTime: dateTime,
        rows
    };
};
addRegisteredSectionLayout({
    name: "SendSummaryByEmailSection",
    displayName: "SendSummaryByEmailSection",
    settings: {}
});
const useStyles = makeStyles(theme => ({
    root: { display: "flex", flexDirection: "column", gap: theme.spacing(1) },
    buttonRow: {
        display: "flex",
        alignItems: "flex-start",
        gap: theme.spacing(1),
        flexWrap: "wrap"
    },
    centeredButtonRow: {
        justifyContent: "center"
    },
    emailInput: { flexGrow: 1, minWidth: 220 },
    successBox: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        color: theme.palette.success?.main ?? theme.palette.primary.main
    }
}));
const messages = defineMessages({
    openButton: {
        id: "ploy-ui.template-form.sendSummaryByEmailSection.openButton",
        defaultMessage: "Send summary by email"
    },
    downloadButton: {
        id: "ploy-ui.template-form.sendSummaryByEmailSection.downloadButton",
        defaultMessage: "Download summary"
    },
    emailPlaceholder: {
        id: "ploy-ui.template-form.sendSummaryByEmailSection.emailPlaceholder",
        defaultMessage: "Email address"
    },
    emailInvalid: {
        id: "ploy-ui.template-form.sendSummaryByEmailSection.emailInvalid",
        defaultMessage: "Invalid email address"
    },
    sendButton: {
        id: "ploy-ui.template-form.sendSummaryByEmailSection.sendButton",
        defaultMessage: "Send"
    },
    cancelButton: {
        id: "ploy-ui.template-form.sendSummaryByEmailSection.cancelButton",
        defaultMessage: "Cancel"
    },
    successMessage: {
        id: "ploy-ui.template-form.sendSummaryByEmailSection.successMessage",
        defaultMessage: "Summary sent by email"
    },
    genericError: {
        id: "ploy-ui.template-form.sendSummaryByEmailSection.genericError",
        defaultMessage: "An error occurred. Please try again."
    }
});
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const postJson = async (url, payload) => fetch(url, {
    method: "POST",
    credentials: "include",
    headers: {
        "Content-Type": "application/json"
    },
    body: JSON.stringify(payload)
});
const readErrorDetail = async (response) => {
    const detail = (await response.json().catch(() => ({})))?.detail;
    return detail || `Request failed with status ${response.status}`;
};
const SendSummaryByEmailSection = (props) => {
    const classes = useStyles(props);
    const intl = useIntl();
    const [state, setState] = useState("idle");
    const [email, setEmail] = useState("");
    const [touched, setTouched] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const isValidEmail = EMAIL_REGEX.test(email);
    const getAccordion = async () => {
        const titleNode = Array.from(document.querySelectorAll("div")).find(node => node.textContent === "Oppsummering");
        if (!titleNode)
            throw new Error("Oppsummering title not found");
        const accordion = titleNode.closest("[class*='MuiAccordion-root']");
        if (!accordion)
            throw new Error("Accordion not found");
        const collapse = accordion.querySelector("[class*='MuiCollapse-root']");
        const isCollapsed = collapse &&
            (!collapse.className.includes("MuiCollapse-entered") ||
                getComputedStyle(collapse).visibility === "hidden");
        let button = null;
        if (isCollapsed) {
            button = accordion.querySelector("[class*='MuiAccordionSummary-root']");
            if (button) {
                button.click();
                await new Promise(resolve => setTimeout(resolve, 10));
            }
        }
        return { accordion, button, wasCollapsed: isCollapsed };
    };
    const extractSummaryLines = (accordion) => {
        const collapseContent = accordion.querySelector("[class*='MuiCollapse-wrapperInner']");
        const rawText = (collapseContent ?? accordion).innerText || "";
        return rawText
            .split(/\r?\n/)
            .map(normalizeText)
            .filter(line => line);
    };
    const generateSummaryPayloadData = async () => {
        const { accordion, button, wasCollapsed } = await getAccordion();
        try {
            const lines = extractSummaryLines(accordion);
            if (lines.length === 0) {
                throw new Error("No summary data found");
            }
            const dateTime = new Intl.DateTimeFormat("nb-NO", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit"
            }).format(new Date());
            return generateSummaryPayload({
                dateTime,
                rows: parseSummaryRows(lines)
            });
        }
        finally {
            if (wasCollapsed && button) {
                button.click();
            }
        }
    };
    const triggerDownload = (blob, filename = SUMMARY_DOWNLOAD_FILENAME) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    };
    const handleDownload = async () => {
        setLoading(true);
        setError(null);
        try {
            const summary = await generateSummaryPayloadData();
            const response = await postJson("/PostprocessSummary/DownloadSummary", {
                summary
            });
            if (!response.ok) {
                throw new Error(await readErrorDetail(response));
            }
            const pdfBlob = await response.blob();
            triggerDownload(pdfBlob);
        }
        catch {
            setError(intl.formatMessage(messages.genericError));
        }
        finally {
            setLoading(false);
        }
    };
    const handleSend = async () => {
        setState("sending");
        setError(null);
        try {
            const summary = await generateSummaryPayloadData();
            const response = await postJson("/PostprocessSummary/SendSummaryByEmail", { recipientEmail: email, summary });
            if (!response.ok) {
                throw new Error(await readErrorDetail(response));
            }
            setState("sent");
        }
        catch {
            setError(intl.formatMessage(messages.genericError));
            setState("form");
        }
    };
    const handleReset = () => {
        setState("idle");
        setEmail("");
        setTouched(false);
        setError(null);
    };
    if (state === "sent") {
        return (_jsxs("div", { className: classes.successBox, onClick: props.onClick, children: [_jsx(CheckCircleOutlineIcon, {}), _jsx(Typography, { children: intl.formatMessage(messages.successMessage) })] }));
    }
    if (state === "idle") {
        return (_jsxs("div", { className: classes.root, children: [_jsxs(Box, { className: `${classes.buttonRow} ${classes.centeredButtonRow}`, children: [_jsx(Button, { variant: "outlined", color: "primary", startIcon: loading ? (_jsx(CircularProgress, { size: 16, color: "inherit" })) : (_jsx(GetAppIcon, {})), onClick: handleDownload, disabled: loading, children: intl.formatMessage(messages.downloadButton) }), _jsx(Button, { variant: "contained", color: "primary", startIcon: _jsx(EmailIcon, {}), disabled: loading, onClick: () => setState("form"), children: intl.formatMessage(messages.openButton) })] }), error && (_jsx(Typography, { variant: "body2", color: "error", children: error }))] }));
    }
    return (_jsxs("div", { className: classes.root, onClick: props.onClick, children: [_jsxs(Box, { className: classes.buttonRow, children: [_jsx(TextField, { className: classes.emailInput, value: email, onChange: e => {
                            setEmail(e.target.value);
                            setTouched(true);
                        }, onBlur: () => setTouched(true), placeholder: intl.formatMessage(messages.emailPlaceholder), variant: "outlined", size: "small", error: touched && !isValidEmail, helperText: touched && !isValidEmail
                            ? intl.formatMessage(messages.emailInvalid)
                            : "", disabled: state === "sending" }), _jsx(Button, { variant: "contained", color: "primary", onClick: handleSend, disabled: !isValidEmail || state === "sending", startIcon: state === "sending" ? (_jsx(CircularProgress, { size: 16, color: "inherit" })) : undefined, children: intl.formatMessage(messages.sendButton) }), state !== "sending" && (_jsx(Button, { variant: "text", onClick: handleReset, children: intl.formatMessage(messages.cancelButton) }))] }), error && (_jsx(Typography, { variant: "body2", color: "error", children: error }))] }));
};
SendSummaryByEmailSection.displayName = "DploySendSummaryByEmailSection";
export { SendSummaryByEmailSection };
//# sourceMappingURL=SendSummaryByEmailSection.js.map